import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Globe, Phone, MapPin, Camera } from "lucide-react";
import BizDetailDialog from "./BizDetailDialog";

const AMENITY = { bathroom: true, parking: true };

function BusinessCard({ b, onOpen }) {
  const img = b.cover_image || b.logo;
  return (
    <div className="rounded-2xl overflow-hidden bg-card border border-border" data-testid={`rider-biz-${b.id}`}>
      <button
        type="button"
        onClick={() => onOpen(b)}
        data-testid={`rider-biz-photo-${b.id}`}
        aria-label={`View details for ${b.name}`}
        className="relative block w-full h-44 bg-secondary cursor-pointer group focus:outline-none"
      >
        {img ? (
          <img src={img} alt={b.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/35 via-primary/10 to-secondary flex items-center justify-center">
            <Camera className="w-8 h-8 text-primary/60" />
          </div>
        )}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
      </button>
      <div className="p-4">
        {b.category && <div className="text-[11px] text-primary font-semibold uppercase tracking-wide">{b.category}</div>}
        <button type="button" onClick={() => onOpen(b)} data-testid={`rider-biz-name-${b.id}`} className="text-left font-heading font-bold text-base leading-tight mt-0.5 hover:text-primary transition-colors">{b.name}</button>
        {b.short_description && <p className="text-sm text-muted-foreground mt-1.5 line-clamp-3">{b.short_description}</p>}
        {(b.website || b.phone) && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border text-xs font-semibold px-3 py-1.5 active:scale-95 transition-transform"><Globe className="w-3.5 h-3.5" />Website</a>}
            {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-border text-xs font-semibold px-3 py-1.5 active:scale-95 transition-transform"><Phone className="w-3.5 h-3.5" />Call</a>}
          </div>
        )}
      </div>
    </div>
  );
}

function AmenityCard({ a }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-3 flex items-start gap-3" data-testid={`rider-amenity-${a.id}`}>
      <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><MapPin className="w-4.5 h-4.5" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-heading font-bold text-sm">{a.name || "Unnamed"}</div>
        {a.notes && <p className="text-xs text-muted-foreground mt-0.5">{a.notes}</p>}
      </div>
    </div>
  );
}

export default function CategoryResults({ pathId, category }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const isAmenity = AMENITY[category];

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSelected(null);
    const req = isAmenity
      ? api.get(`/rider/bike-paths/${pathId}/amenities`, { params: { type: category } })
      : api.get(`/rider/bike-paths/${pathId}/businesses`, { params: { category } });
    req.then(({ data }) => { if (active) setItems(data.items || []); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [pathId, category, isAmenity]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  if (items.length === 0) return <div className="py-10 text-center text-sm text-muted-foreground" data-testid="rider-cat-empty">Nothing here yet along this path.</div>;

  return (
    <div className="space-y-3" data-testid="rider-cat-results">
      {items.map((it) => isAmenity ? <AmenityCard key={it.id} a={it} /> : <BusinessCard key={it.id} b={it} onOpen={setSelected} />)}
      <BizDetailDialog biz={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
